import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ConfirmedOrderItem } from './types';
import { formatMoney, formatQuantity } from './cartUtils';

interface CheckoutResultProps {
  orderNumber: string;
  items: ConfirmedOrderItem[];
  totalValue: number;
  currency: string;
  deliveryDay: string;
  deliveryDate: string;
  buyerName: string;
  onContinueShopping: () => void;
}

const getEmoji = (title: string) => {
  const t = (title || '').toLowerCase();
  if (t.includes('avocado')) return '🥑';
  if (t.includes('macadamia')) return '🌰';
  if (t.includes('tomato')) return '🍅';
  if (t.includes('mango')) return '🥭';
  if (t.includes('pineapple')) return '🍍';
  if (t.includes('banana')) return '🍌';
  if (t.includes('coffee')) return '☕';
  if (t.includes('tea')) return '🍵';
  return '📦';
};

const CheckoutResult: React.FC<CheckoutResultProps> = ({
  orderNumber, items, totalValue, currency, deliveryDay, deliveryDate, buyerName, onContinueShopping,
}) => {
  const formattedDeliveryDate = deliveryDate
    ? new Date(deliveryDate).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  return (
    <div className="max-w-2xl mx-auto text-center">
      {/* Success Animation */}
      <div className="mb-8">
        <div className="relative inline-block">
          <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-200 animate-bounce-once">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-lg animate-bounce">✨</div>
        </div>
        <h1 className="text-4xl font-extrabold text-gray-900 mt-6 mb-2">Order Confirmed!</h1>
        <p className="text-gray-500 text-lg">Thank you, <span className="font-bold text-gray-900">{buyerName}</span></p>
      </div>

      {/* Order Details Card */}
      <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/50 overflow-hidden text-left mb-8 border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-6 text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Order Number</p>
              <p className="text-2xl font-extrabold mt-1">{orderNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-emerald-100 text-sm font-medium">Total</p>
              <p className="text-2xl font-extrabold mt-1">{formatMoney(totalValue, currency)}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Items */}
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Order Items</h3>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={item.listingId || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-lg">
                      {getEmoji(item.title)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm">{item.title}</p>
                      <p className="text-xs text-gray-500">
                        {formatQuantity(item.quantity, item.unit)}
                        {item.grade ? ` • ${item.grade}` : ''}
                      </p>
                    </div>
                  </div>
                  <p className="font-bold text-gray-900">{formatMoney(item.subtotal, item.currency)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery */}
          <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-blue-900">Expected Delivery</p>
                {formattedDeliveryDate ? (
                  <p className="text-blue-700 text-sm">{formattedDeliveryDate}</p>
                ) : (
                  <p className="text-blue-700 text-sm">{deliveryDay ? `Preferred day: ${deliveryDay}` : 'To be confirmed by the supplier'}</p>
                )}
                <p className="text-blue-500 text-xs mt-1">Delivery details are confirmed by the supplier after order confirmation.</p>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-amber-900">Payment</p>
                <p className="text-amber-600 text-xs mt-1">Payment is arranged after the supplier confirms your order.</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-gray-900">Questions about your order?</p>
                <p className="text-gray-600 text-sm">Contact the eSanza marketplace team for support with this order.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/buy"
          className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-2xl font-extrabold text-lg hover:from-emerald-600 hover:to-green-700 shadow-xl shadow-emerald-200 hover:shadow-2xl transition-all hover:-translate-y-0.5"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Continue Shopping
        </Link>
        <button
          onClick={onContinueShopping}
          className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-700 rounded-2xl font-bold text-lg border-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all"
        >
          View My Orders
        </button>
      </div>

      <style>{`
        @keyframes bounce-once {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-once {
          animation: bounce-once 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CheckoutResult;
